import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  SetStateAction,
  Dispatch,
} from "react";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  SORT_DIRECTION,
  LEVEL_TYPE,
  CustomError,
  BasePagination,
} from "api/interfaces/commonInterface.interface";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { TableColumnsType } from "antd";
import {
  KeyAccount,
  SUBSCRIPTION_TYPE,
  SubscriptionKeyListReqeust,
  SubscriptionKeyListType,
  SubscriptionKeyListResponse,
  ChangeWidthColumn,
  SubscriptionPageOutletContext,
  GetSubscriptionKeyResponse,
  SubscriptionKeyDateType,
} from "api/interfaces/subscription.interface";
import {
  StatusLabel,
  StatusName,
  StatusValue,
} from "components/blocks/status/Status";
import { COLORS } from "styles/colors";
import BigButton from "components/atoms/buttons/BigButton";
import { ReactComponent as KEY_ADD_ICON } from "assets/icons/key_add.svg";
import { SubscriptionKeyListStyled } from "../SubscriptionStyled";
import { ReactComponent as EXPORT_ICON } from "assets/icons/export.svg";
import SubscriptionKeyListPresenter from "./SubscriptionKeyListPresenter";
import { excelDateFormat, getAccountTimeFormatDate } from "utils/timeUtil";
import FileSaver from "file-saver";
import AccountInfoLabel from "components/atoms/text/labels/AccountInfoLabel";
import {
  getActivateStatus,
  getEffectiveStatus,
  showActivateStatus,
} from "utils/SubscriptionUtil";
import {
  initialQueryKeyInfo,
  useKeyTable,
} from "hook/subscription/useKeyTable";
import useStatusMutation from "hook/useStatusMutation";
import {
  deleteSubscriptionKey,
  getSubscriptionKeyList,
} from "api/subscriptionAPI";
import useApiError from "hook/useApiError";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { changeSortOrderAntd } from "components/atoms/table/AntdTable";
import TableColumnFilterTitle from "components/blocks/table/TableColumnFilterTitle";
import { useKeyType } from "hook/subscription/useKeyType";
import { ColumnType } from "antd/es/table";
import moment from "moment";
import { FaRegTrashAlt } from "react-icons/fa";
import * as mediaQuery from "components/MediaQuery";
import { useMediaQuery } from "react-responsive";
import * as XLSX from "xlsx";
import { formatNumber, formatUppercaseOnly } from "utils/functions";
import InputDate from "components/atoms/calendar/InputDate";
import * as s from "../SubscriptionStyled";
import SelectComponent from "components/atoms/select/SelectComponent";

type Props = {};

const dateTypeOptions: { label: string; value: SubscriptionKeyDateType }[] = [
  { label: "Created Date", value: "createdAt" },
  { label: "Registered Date", value: "registerDate" },
  { label: "Expiration Date", value: "expireDate" },
  { label: "Activated Date", value: "activateDate" },
];

export interface SubscriptionKeyListReqeustDate
  extends SubscriptionKeyListReqeust {
  fromDate: string;
  toDate: string;
}
const getInitialQueryDate = (
  timezone: string,
  isDW: boolean
): SubscriptionKeyListReqeustDate => {
  const today = moment().format("YYYY-MM-DD HH:mm:ss");
  const defaultDateType: SubscriptionKeyDateType = isDW
    ? "createdAt"
    : "registerDate";
  return {
    ...initialQueryKeyInfo,
    sortType: defaultDateType,
    dateType: defaultDateType,
    fromDate: moment
      .tz(today, timezone)
      .startOf("day")
      .add(-7, "days")
      .utc()
      .format("YYYY-MM-DDTHH:mm:ss"),
    toDate: moment
      .tz(today, timezone)
      .endOf("day")
      .utc()
      .format("YYYY-MM-DDTHH:mm:ss"),
  };
};
export default function SubscriptionKeyListContainer(
  props: Props
): JSX.Element {
  const { statusOptions, durationMonthOptions } = useKeyType();
  const currentDateFilterWrapper = useRef<HTMLDivElement>(null);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const isDW = selectedAccount.accountLevel === LEVEL_TYPE.DW;
  const isCP = selectedAccount.accountLevel === LEVEL_TYPE.CP;
  const isSubCp = isCP && selectedAccount.parentLevel === LEVEL_TYPE.CP;
  const breakpointLg = useMediaQuery({ maxWidth: 1500 });
  const bigScreen = mediaQuery.useIsBigScreen();

  const { handleErrorNotify, notifyError, lang } = useApiError();

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);

  const [isModal, setIsModal] = useState<{
    [key: string]: boolean | SubscriptionKeyListType;
  }>({
    register: false,
    generate: false,
    replace: false,
    confirmDelete: false,
  });

  const {
    onHeaderCell,
    onChangePage,
    setButtons,
    onClickFilterTitle,
    setStatus,
    setFilters,
    updateQueryInfo,
  } = useOutletContext<SubscriptionPageOutletContext>();
  const [pageInfo, setPageInfo] = useState<
    Partial<SubscriptionKeyListReqeustDate>
  >(() => getInitialQueryDate(selectedAccount.timezone, isDW));
  const [searchParams, setSearchParams] = useSearchParams();
  const queryInfo = useMemo<BasePagination>(() => {
    const get = (key: string) => searchParams.get(key);

    return {
      ...initialQueryKeyInfo,
      ...pageInfo,
      ...(get("pageNum") && { pageNum: Number(get("pageNum")) }),
      ...(get("pageLimit") && { pageLimit: Number(get("pageLimit")) }),
      ...{ keyword: get("keyword") ?? "" },
      ...(get("sortType") && { sortType: get("sortType")! }),
      ...(get("sortDirection") && {
        sortDirection: get("sortDirection") as SORT_DIRECTION,
      }),
      ...(get("fromDate") && { fromDate: get("fromDate")! }),
      ...(get("toDate") && { toDate: get("toDate")! }),
      ...(get("dateType") && {
        dateType: get("dateType") as SubscriptionKeyDateType,
      }),
      ...(get("status") && { status: get("status")! }),
      ...(get("type") && { type: get("type") }),
      ...(get("rowAccountId") && { rowAccountId: get("rowAccountId")! }),
      ...(get("rowAccountNumber") && {
        rowAccountNumber: get("rowAccountNumber")!,
      }),
      ...(get("rowAccountLevel") && {
        rowAccountLevel: get("rowAccountLevel")!,
      }),
      ...(get("rowParentLevel") && { rowParentLevel: get("rowParentLevel")! }),
    };
  }, [searchParams, pageInfo]);
  const {
    subscriptionKeyList,
    subscriptionKeySummary,
    useQueryKeyList,
    commonKeyColumns,
    changeWidthColumn,
  } = useKeyTable({
    queryInfo: queryInfo as SubscriptionKeyListReqeust,
    setQueryInfo: setPageInfo as Dispatch<
      SetStateAction<SubscriptionKeyListReqeust>
    >,
  });

  const convertData = useCallback(
    (res: SubscriptionKeyListResponse, idx: number) => {
      let purchased = res.channel;

      let tempCp: KeyAccount | null = null;
      let tempSubCp: KeyAccount | null = null;
      let tempEu: KeyAccount | null = null;

      if (res.useAccount && res.euAccount) {
        tempEu = {
          ...res.euAccount,
        };
        if (res.euAccount.cpAccount) {
          tempCp = {
            ...res.euAccount.cpAccount,
          };
        }
        if (res.euAccount.subAccount) {
          tempSubCp = {
            ...res.euAccount.subAccount,
          };
        }
      }
      if (!res.useAccount && res.parentAccount) {
        const parentAccount = {
          ...(res.parentAccount as unknown as KeyAccount),
          accountNumber: res.parentAccount.accountNumber,
          name: res.parentAccount.name,
          cpId: res.parentAccount.parentId || "",
          subId: "",
          euId: "",
        };
        if (isDW && res.parentAccount.parentLevel === LEVEL_TYPE.DW) {
          tempCp = parentAccount;
        }
        if (isCP || res.parentAccount.parentLevel === LEVEL_TYPE.CP) {
          tempSubCp = parentAccount;
        }
      }

      if (res.type === SUBSCRIPTION_TYPE.STORAGE) {
        purchased = res.capacity;
      }

      return {
        ...res,
        key: `key-${idx}`,
        purchased,
        status: getActivateStatus(res),
        ...(tempCp && {
          CP: { ...(tempCp as KeyAccount) },
        }),
        ...(tempSubCp && {
          SubCP: { ...(tempSubCp as KeyAccount) },
        }),
        ...(tempEu && {
          EU: { ...(tempEu as KeyAccount) },
        }),
      };
    },
    [isCP, isDW]
  );

  const data: SubscriptionKeyListType[] = useMemo(() => {
    let tempData: SubscriptionKeyListType[] = subscriptionKeyList.map(
      (res: SubscriptionKeyListResponse, idx: number) => convertData(res, idx)
    );

    return tempData;
  }, [convertData, subscriptionKeyList]);

  const insertIndex = 6;
  const fixWidthCell: ChangeWidthColumn[] = [
    {
      dataIndex: "type",
      width: isDW ? 70 : 100,
      responsive: isDW ? ["xxl"] : ["lg"],
    },
    {
      dataIndex: "duration",
      width: bigScreen ? 140 : breakpointLg ? 75 : 140,
    },
    {
      dataIndex: "createdAt",
      width: breakpointLg ? 110 : "10%",
      responsive: ["lg"],
    },

    {
      dataIndex: "actualPrice",
      width: bigScreen ? 120 : breakpointLg ? 75 : 90,
      responsive: ["lg"],
    },
    {
      dataIndex: "purchased",
      width: 75,
    },
  ];

  const renderAccountColumn = (
    dataIndex: ("CP" | "SubCP" | "EU")[],
    hidden?: boolean
  ): ColumnType<SubscriptionKeyListType> => {
    return {
      title: (
        <SubscriptionKeyListStyled.CpWrapper className="table-header-column">
          {dataIndex.map((index) => (
            <div key={index} className={index}>
              {index === "SubCP" ? "Sub CP" : index}
              {index === "CP" && (
                <span
                  className={`sort-icon ${
                    queryInfo.sortType === "cp" &&
                    (queryInfo.sortDirection === SORT_DIRECTION.ASC
                      ? "asc"
                      : "desc")
                  }`}
                ></span>
              )}
            </div>
          ))}
        </SubscriptionKeyListStyled.CpWrapper>
      ),

      render: (value: any, row: SubscriptionKeyListType) => {
        return (
          <SubscriptionKeyListStyled.CpWrapper className="table-cell-column">
            {dataIndex.map((index: "CP" | "SubCP" | "EU") => {
              if (
                row[index] &&
                row[index]?.accountNumber !== selectedAccount.accountNumber
              ) {
                return (
                  <div key={index} className={index}>
                    <AccountInfoLabel
                      level={index === "EU" ? LEVEL_TYPE.EU : LEVEL_TYPE.CP}
                      accountNumber={row[index]?.accountNumber as string}
                      accountName={row[index]?.name as string}
                      direction="column"
                    />
                  </div>
                );
              }
              return (
                <div key={index} className={index}>
                  -
                </div>
              );
            })}
          </SubscriptionKeyListStyled.CpWrapper>
        );
      },
      key: "cp",
      sortOrder: dataIndex.includes("CP")
        ? changeSortOrderAntd(queryInfo, "cp")
        : undefined,
      defaultSortOrder: "ascend",
      onHeaderCell: dataIndex.includes("CP") ? onHeaderCell : undefined,
      width: bigScreen ? "20%" : dataIndex.length * 100,
    };
  };

  const columns: TableColumnsType<SubscriptionKeyListType> = [
    ...changeWidthColumn(
      commonKeyColumns.slice(
        0,
        insertIndex
      ) as TableColumnsType<SubscriptionKeyListType>,
      fixWidthCell
    ),
    commonKeyColumns[insertIndex],
    {
      title: "Reference",
      dataIndex: "reference",
      render: (value, row) => value,
      hidden: !bigScreen || !isDW,
      responsive: ["xxl"],
      width: bigScreen ? undefined : 82,
    },
    {
      title: "Distributor",
      dataIndex: "distributor",
      render: (value) => value,
      hidden: !bigScreen || !isDW,
      responsive: ["xxl"],
      width: bigScreen ? undefined : 82,
    },
    renderAccountColumn(
      breakpointLg
        ? ["EU"]
        : isDW
        ? ["CP", "SubCP", "EU"]
        : isSubCp
        ? ["EU"]
        : ["SubCP", "EU"]
    ),
    {
      ...commonKeyColumns[insertIndex + 1],
      width: 90,
    },
    commonKeyColumns[insertIndex + 2],
    {
      title: (
        <TableColumnFilterTitle
          title="Status"
          filters={statusOptions}
          onClick={(value: string | undefined) => {
            onClickFilterTitle(value, "status");
          }}
          data-testid="subscription-keys-keys-table-column-filter-title-click-filter-title"
        />
      ),

      dataIndex: "status",
      render: (value, row) => {
        // extendTargetKey 가 있으면 만료 경고 상태를 무시하고 ACTIVATED 로 표시
        const effectiveStatus = getEffectiveStatus(value, row.extendTargetKey);
        return (
          <SubscriptionKeyListStyled.ExpireDateBox status={effectiveStatus}>
            {showActivateStatus(effectiveStatus)}
          </SubscriptionKeyListStyled.ExpireDateBox>
        );
      },

      width: breakpointLg ? 70 : 80,
    },
    {
      title: "",
      render: (value, row) => {
        if (!(isDW && (row.CP || row.EU || row.SubCP)) && !row.activateDate) {
          return (
            <button
              onClick={() =>
                setIsModal((mod) => {
                  return {
                    ...mod,
                    confirmDelete: row,
                  };
                })
              }
              data-testid="subscription-keys-keys-button-set-is-modal"
            >
              <FaRegTrashAlt color={COLORS.RED} />
            </button>
          );
        }

        return <></>;
      },
      align: "right",
      width: 30,
      hidden: !isDW,
    },
  ];
  useEffect(() => {
    setStatus({
      totalTitle: "Total",
      totalValue: queryInfo.total,
      items: [
        <>
          <StatusLabel>
            <StatusName>Expiring Soon</StatusName>
          </StatusLabel>
          <StatusValue color={COLORS.ORANGE}>
            {subscriptionKeySummary.expireSoon}
          </StatusValue>
        </>,
        <>
          <StatusLabel>
            <StatusName>Expired</StatusName>
          </StatusLabel>
          <StatusValue color={COLORS.RED}>
            {subscriptionKeySummary.expired}
          </StatusValue>
        </>,
      ],

      isMobileShow: {
        totalTitle: true,
        totalValue: true,
        items: false,
      },
    });
  }, [
    queryInfo.total,
    setStatus,
    subscriptionKeySummary.expireSoon,
    subscriptionKeySummary.expired,
  ]);

  const excelFileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const excelFileExtension = ".xlsx";
  const excelFileName = `keys-${excelDateFormat()}`;

  const excelKeys: string[] = [
    "Key",
    "Type",
    "Purchased",
    "Duration",
    "Price",
    "Created Date",
    "Created By",
    "Reference Number",
    "Distributor",
    "CP Name",
    "CP ID",
    "Sub CP Name",
    "Sub CP ID",
    "EU Name",
    "EU ID",
    "Activated",
    "Expiration",
    "Status",
  ];

  const onDownloadExcel = useCallback(() => {
    // console.log({matchInfoArray});
    if (!pageInfo.total) {
      notifyError(lang.label.subscription.export.noData);
      return;
    }

    const ws = XLSX.utils.aoa_to_sheet([excelKeys]);

    getSubscriptionKeyList({
      accountId: selectedAccount.accountId,
      payload: {
        ...initialQueryKeyInfo,
        pageLimit: pageInfo.total,
      } as SubscriptionKeyListReqeust,
    })
      .then((res: GetSubscriptionKeyResponse) => {
        (res.result.list ?? [])
          .map((data: SubscriptionKeyListResponse, idx: number) =>
            convertData(data, idx)
          )
          .map((data: SubscriptionKeyListType) => {
            const purchaseRender = () => {
              let unit = "CH";
              if (data.type === SUBSCRIPTION_TYPE.STORAGE) {
                unit = "TB";
              }
              return `${formatNumber(data.purchased)} ${unit}`;
            };
            const priceRender = () => {
              if (data.isFreeKey) {
                return "Free";
              }

              const price = Number(data.actualPrice);

              if (Number.isNaN(price)) {
                return "-";
              }

              const fixedPrice = Number(price.toFixed(2));

              return `$${formatNumber(fixedPrice)}`;
            };
            const durationRender = () => {
              const duration = (
                durationMonthOptions(data.type as SUBSCRIPTION_TYPE, true).find(
                  (opt) => opt.value === String(data.duration)
                )?.label || "-"
              ).split("(");
              const durationLabel = duration[0];
              return durationLabel;
            };
            let sheetArr = [
              data.dwKey,
              formatUppercaseOnly(data.type),
              purchaseRender(),
              durationRender(),
              priceRender(),
              getAccountTimeFormatDate(data.createdAt, selectedAccount),
              data.user?.name || "System",
              data.reference,
              data.distributor,
              data.CP?.name || "-",
              data.CP?.accountNumber || "-",
              data.SubCP?.name || "-",
              data.SubCP?.accountNumber || "-",
              data.EU?.name || "-",
              data.EU?.accountNumber || "-",
              data.activateDate
                ? getAccountTimeFormatDate(data.activateDate, selectedAccount)
                : "-",
              data.expireDate
                ? getAccountTimeFormatDate(data.expireDate, selectedAccount)
                : "-",
              showActivateStatus(data.status),
            ];

            let cols = [
              { wpx: 130 },
              { wpx: 70 },
              { wpx: 60 },
              { wpx: 60 },
              { wpx: 100 },
              { wpx: 80 },
              { wpx: 100 },
              { wpx: 100 },
              { wpx: 100 },
              { wpx: 100 },
              { wpx: 100 },
              { wpx: 100 },
              { wpx: 100 },
              { wpx: 100 },
              { wpx: 100 },
              { wpx: 80 },
              { wpx: 80 },
              { wpx: 60 },
            ];

            XLSX.utils.sheet_add_aoa(ws, [sheetArr as any[]], { origin: -1 });
            ws["!cols"] = cols;
            return false;
          });
        const wb: any = {
          Sheets: { Keys: ws },
          SheetNames: ["Keys"],
        };
        // console.log(wb);
        const excelButter = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const excelFile = new Blob([excelButter], { type: excelFileType });
        FileSaver.saveAs(excelFile, excelFileName + excelFileExtension);
      })
      .catch((err) => {
        handleErrorNotify(err, lang.label.subscription.export.fail);
      });
  }, [
    excelKeys,
    selectedAccount,
    queryInfo,
    pageInfo,
    excelFileName,
    convertData,
    durationMonthOptions,
    notifyError,
    handleErrorNotify,
    lang,
  ]);

  const onExport = useCallback(() => {
    if (isDW) {
      onDownloadExcel();
    }
  }, [isDW, onDownloadExcel]);

  useEffect(() => {
    if (!selectedAccount.accountId) return;
    const fromDate = getInitialQueryDate(
      selectedAccount.timezone,
      isDW
    ).fromDate;
    const toDate = getInitialQueryDate(selectedAccount.timezone, isDW).toDate;
    const dateType =
      (queryInfo as SubscriptionKeyListReqeust).dateType ??
      (isDW ? "createdAt" : "registerDate");
    const dateTypeLabel =
      dateTypeOptions.find((option) => option.value === dateType)?.label ??
      (isDW ? "Created Date" : "Registered Date");
    // eslint-disable-next-line react-hooks/rules-of-hooks
    setFilters(
      <s.SubscriptionKeyListStyled.DateFilterGroup
        ref={currentDateFilterWrapper}
      >
        <s.SubscriptionKeyListStyled.DateTypeSelectWrapper>
          <SelectComponent
            value={dateType}
            options={dateTypeOptions}
            onChange={(value: SubscriptionKeyDateType) => {
              const dateTypeUpdate = { pageNum: 0, dateType: value };
              updateQueryInfo(dateTypeUpdate);
            }}
            data-testid="subscription-keys-keys-select-date-type"
          />
        </s.SubscriptionKeyListStyled.DateTypeSelectWrapper>
        <s.SubscriptionKeyListStyled.CalendarWrapper>
          <InputDate
            mode="range"
            tooltip={`${dateTypeLabel} Range`}
            value={[fromDate, toDate]}
            onChange={(date) => {
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("fromDate", date?.[0] ?? "");
                next.set("toDate", date?.[1] ?? "");
                return next;
              });
            }}
            maxDate={new Date()}
            required
          />
        </s.SubscriptionKeyListStyled.CalendarWrapper>
      </s.SubscriptionKeyListStyled.DateFilterGroup>
    );
    setButtons(
      <RBACWrapper
        requiredPermissions={
          isDW
            ? "generateKeys"
            : ["manageBillingAndLicenses", "manageSubscription"]
        }
      >
        <SubscriptionKeyListStyled.ButtonWrapper>
          {isDW && (
            <>
              <BigButton
                label={"Export"}
                buttonType="cancel"
                onClickBtn={onExport}
                iconComponent={<EXPORT_ICON />}
                // disabled={!mutateExportKeyList.idle}
                data-testid="subscription-keys-keys-button-export"
              />
              <BigButton
                label={"Generate Keys"}
                onClickBtn={() => {
                  setIsModal({
                    ...isModal,
                    generate: true,
                  });
                }}
                iconComponent={<KEY_ADD_ICON />}
                data-testid="subscription-keys-keys-button-set-is-modal"
              />
            </>
          )}

          {isCP && (
            <BigButton
              add
              label={"Register Keys"}
              onClickBtn={() => {
                setIsModal({
                  ...isModal,
                  register: true,
                });
              }}
            />
          )}
        </SubscriptionKeyListStyled.ButtonWrapper>
      </RBACWrapper>
    );
  }, [
    isDW,
    isCP,
    setButtons,
    (queryInfo as SubscriptionKeyListReqeust).dateType,
    (queryInfo as SubscriptionKeyListReqeust).total,
    updateQueryInfo,
  ]);

  const onChangeModal = (key: string) => {
    setIsModal((mod) => {
      return {
        ...mod,
        [key]: false,
      };
    });
  };

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (window.innerWidth > 1399 || window.innerWidth < 1100) return;
      if (!currentDateFilterWrapper.current) return;
      if (
        isFilterOpen &&
        !currentDateFilterWrapper.current?.contains(e.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    },
    [isFilterOpen, currentDateFilterWrapper]
  );

  const mutationDeleteDwKey = useStatusMutation(deleteSubscriptionKey, {
    onSuccess: () => {
      onChangeModal("confirmDelete");
      useQueryKeyList.refetch();
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.report.notify.cvv.delete.fail);
    },
  });

  const onConfirmDelete = () => {
    if (typeof isModal.confirmDelete !== "boolean" && isModal.confirmDelete) {
      mutationDeleteDwKey.mutate(isModal.confirmDelete.dwKey);
    }
  };

  useEffect(() => {
    window.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <SubscriptionKeyListPresenter
      queryInfo={queryInfo as SubscriptionKeyListReqeustDate}
      columns={columns}
      data={data}
      onChangePage={(page: number) => onChangePage(page, 0)}
      isFetching={useQueryKeyList.isFetching}
      refetch={useQueryKeyList.refetch}
      isModal={isModal}
      onChangeModal={onChangeModal}
      onConfirmDelete={onConfirmDelete}
    />
  );
}
